"use client";

import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
    Users, Briefcase, Shield, Globe, Euro, HelpCircle,
    CheckCircle, ArrowRight, Zap, Clock, MapPin, Code2,
    Palette, BarChart2, Megaphone, UserCheck, XCircle
} from "lucide-react";
import Link from "next/link";
import CookieBanner from "@/components/CookieBanner";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

export const metadata = {
    title: "FreelanceGen – Geprüfte IT-Freelancer aus DACH | Direkt",
    description: "Erste Kandidaten in 24–48h. Geprüfte IT-Profile aus DE, AT, CH – direkt kontaktierbar, 0 % Provision, kein Agenturumweg.",
    keywords: "IT Freelancer finden DACH, Freelancer Deutschland, geprüfte Entwickler freelance, Senior Freelancer DACH direkt",
};

const whyItems = {
    de: [
        {
            icon: <Zap className="h-6 w-6 text-blue-600" />,
            title: "Kein wochenlanger Prozess",
            desc: "Erste passende Profile in der Regel innerhalb von 24–48 Stunden – nicht nach zwei Recruiting-Runden.",
        },
        {
            icon: <CheckCircle className="h-6 w-6 text-green-600" />,
            title: "Weniger schlechte Bewerbungen",
            desc: "Manuell geprüfte Profile mit hoher Eingangshürde. Kein offener Pool, kein automatisches Zulassen.",
        },
        {
            icon: <Shield className="h-6 w-6 text-teal-600" />,
            title: "Profil vor dem ersten Gespräch",
            desc: "Skills, Erfahrung und Verfügbarkeit können Sie vorab einsehen – kein Blindgespräch.",
        },
        {
            icon: <Euro className="h-6 w-6 text-green-700" />,
            title: "0 % Provision",
            desc: "Kein Agenturaufschlag. Was Sie mit dem Freelancer vereinbaren, bleibt zu 100 % zwischen Ihnen.",
        },
        {
            icon: <Users className="h-6 w-6 text-blue-700" />,
            title: "Kein Mittelsmann",
            desc: "Sie kontaktieren direkt – ohne Rückfragen über Dritte, ohne Verzögerungen durch Vermittlung.",
        },
        {
            icon: <Globe className="h-6 w-6 text-purple-600" />,
            title: "DACH-Zeitzone, DSGVO-konform",
            desc: "Alle Profile aus DE, AT, CH. Gleiche Zeitzone, kurze Abstimmungswege. Daten nur auf EU-Servern.",
        },
    ],
    en: [
        {
            icon: <Zap className="h-6 w-6 text-blue-600" />,
            title: "No weeks-long process",
            desc: "First matching profiles typically within 24–48 hours – not after two recruiting rounds.",
        },
        {
            icon: <CheckCircle className="h-6 w-6 text-green-600" />,
            title: "Fewer poor applications",
            desc: "Manually vetted profiles with a high entry bar. No open pool, no automatic admissions.",
        },
        {
            icon: <Shield className="h-6 w-6 text-teal-600" />,
            title: "Profile before the first call",
            desc: "You can review skills, experience, and availability upfront – no blind interviews.",
        },
        {
            icon: <Euro className="h-6 w-6 text-green-700" />,
            title: "0% commission",
            desc: "No agency markup. What you agree with the freelancer stays 100% between you.",
        },
        {
            icon: <Users className="h-6 w-6 text-blue-700" />,
            title: "No middleman",
            desc: "Contact directly – no third-party callbacks, no delays through intermediaries.",
        },
        {
            icon: <Globe className="h-6 w-6 text-purple-600" />,
            title: "DACH timezone, GDPR-compliant",
            desc: "All profiles from DE, AT, CH. Same timezone, short coordination paths. Data only on EU servers.",
        },
    ],
};

const useCases = {
    de: [
        {
            icon: <Code2 className="h-6 w-6 text-blue-600" />,
            label: "Software Development",
            desc: "Frontend, Backend, Fullstack, Mobile, DevOps, AI-Engineering – projektbasiert oder laufend.",
        },
        {
            icon: <Palette className="h-6 w-6 text-purple-600" />,
            label: "Design & UX",
            desc: "Produktdesign, UX-Research, Figma-Prototyping – von der ersten Skizze bis zum fertigen Interface.",
        },
        {
            icon: <Megaphone className="h-6 w-6 text-orange-500" />,
            label: "Marketing",
            desc: "SEO, Performance, Content, Social – schnell skalierbar, keine Langzeitverpflichtung.",
        },
        {
            icon: <BarChart2 className="h-6 w-6 text-green-600" />,
            label: "Data & Analytics",
            desc: "Datenmodellierung, BI-Dashboards, ML – Entscheidungsgrundlagen statt Bauchgefühl.",
        },
        {
            icon: <UserCheck className="h-6 w-6 text-teal-600" />,
            label: "HR & Interim",
            desc: "Interim HR, Talent Acquisition, People Ops – Kapazität genau dann, wenn Sie sie brauchen.",
        },
    ],
    en: [
        {
            icon: <Code2 className="h-6 w-6 text-blue-600" />,
            label: "Software Development",
            desc: "Frontend, Backend, Fullstack, Mobile, DevOps, AI Engineering – project-based or ongoing.",
        },
        {
            icon: <Palette className="h-6 w-6 text-purple-600" />,
            label: "Design & UX",
            desc: "Product design, UX research, Figma prototyping – from first sketch to finished interface.",
        },
        {
            icon: <Megaphone className="h-6 w-6 text-orange-500" />,
            label: "Marketing",
            desc: "SEO, Performance, Content, Social – quickly scalable, no long-term commitment.",
        },
        {
            icon: <BarChart2 className="h-6 w-6 text-green-600" />,
            label: "Data & Analytics",
            desc: "Data modeling, BI dashboards, ML – decisions based on data, not gut feeling.",
        },
        {
            icon: <UserCheck className="h-6 w-6 text-teal-600" />,
            label: "HR & Interim",
            desc: "Interim HR, Talent Acquisition, People Ops – capacity exactly when you need it.",
        },
    ],
};

const workModels = {
    de: [
        { label: "Remote", desc: "Vollständig ortsunabhängig" },
        { label: "Hybrid", desc: "2–3 Tage vor Ort, Rest remote" },
        { label: "Vor Ort", desc: "Vollständig im Büro" },
    ],
    en: [
        { label: "Remote", desc: "Fully location-independent" },
        { label: "Hybrid", desc: "2–3 days on-site, rest remote" },
        { label: "On-site", desc: "Fully in the office" },
    ],
};

const typicalProjects = {
    de: [
        { title: "Senior React / Next.js Entwickler", location: "Remote", rate: "82–96 €/h", label: "Typisch: 2–5 Tage bis Start" },
        { title: "Fullstack .NET Entwickler", location: "Hybrid Stuttgart", rate: "78–90 €/h", label: "Typisch: 3–6 Tage bis Start" },
        { title: "Python Data Engineer (Azure)", location: "Remote", rate: "85–105 €/h", label: "Typisch: 2–4 Tage bis Start" },
        { title: "UX Designer (Figma)", location: "München Hybrid", rate: "65–80 €/h", label: "Typisch: 4–7 Tage bis Start" },
        { title: "DevOps Engineer (Kubernetes)", location: "Remote", rate: "80–95 €/h", label: "Typisch: 3–5 Tage bis Start" },
        { title: "Java Backend Entwickler", location: "Hybrid Zürich", rate: "88–110 €/h", label: "Typisch: 3–6 Tage bis Start" },
    ],
    en: [
        { title: "Senior React / Next.js Developer", location: "Remote", rate: "82–96 €/h", label: "Typically: 2–5 days to start" },
        { title: "Fullstack .NET Developer", location: "Hybrid Stuttgart", rate: "78–90 €/h", label: "Typically: 3–6 days to start" },
        { title: "Python Data Engineer (Azure)", location: "Remote", rate: "85–105 €/h", label: "Typically: 2–4 days to start" },
        { title: "UX Designer (Figma)", location: "Munich Hybrid", rate: "65–80 €/h", label: "Typically: 4–7 days to start" },
        { title: "DevOps Engineer (Kubernetes)", location: "Remote", rate: "80–95 €/h", label: "Typically: 3–5 days to start" },
        { title: "Java Backend Developer", location: "Hybrid Zurich", rate: "88–110 €/h", label: "Typically: 3–6 days to start" },
    ],
};

const steps = {
    de: [
        {
            step: "01",
            title: "Bedarf beschreiben",
            desc: "Skills, Zeitrahmen, Arbeitsmodell. Kein langer Onboarding-Prozess – in Minuten einsatzbereit.",
        },
        {
            step: "02",
            title: "Passende Profile sehen",
            desc: "Geprüfte Profile aus DACH – in der Regel innerhalb von 24–48 Stunden. Sie wählen selbst.",
        },
        {
            step: "03",
            title: "Direkt loslegen",
            desc: "Sie kontaktieren, Sie verhandeln, Sie beauftragen. Kein Dritter, der mitentscheidet.",
        },
    ],
    en: [
        {
            step: "01",
            title: "Describe your need",
            desc: "Skills, timeline, work model. No lengthy onboarding process – up and running in minutes.",
        },
        {
            step: "02",
            title: "See matching profiles",
            desc: "Vetted profiles from DACH – typically within 24–48 hours. You choose yourself.",
        },
        {
            step: "03",
            title: "Get started directly",
            desc: "You contact, you negotiate, you hire. No third party co-deciding.",
        },
    ],
};

const faqs = {
    de: [
        {
            q: "Wie schnell sehe ich erste Kandidaten?",
            a: "Das hängt vom gesuchten Profil ab. In der Regel melden sich erste passende Freelancer innerhalb von 24–48 Stunden nach Veröffentlichung.",
        },
        {
            q: "Was kostet mich das als Unternehmen?",
            a: "1 Projektveröffentlichung pro Monat ist kostenlos. Weitere Projektveröffentlichungen können ab 49 € einzeln oder im Paket erworben werden. Für die Zusammenarbeit mit Freelancern berechnen wir keine Provision.",
        },
        {
            q: "Wie werden die Profile geprüft?",
            a: "Jedes Profil durchläuft einen manuellen Onboarding-Prozess: Skills, Erfahrung und Profilvollständigkeit werden geprüft.",
        },
        {
            q: "Ist die Plattform DSGVO-konform?",
            a: "Ja. Alle Daten werden ausschließlich auf EU-Servern gespeichert. DSGVO-Konformität ist kein Add-on – sie ist Standard.",
        },
        {
            q: "Für welche Rollen kann ich posten?",
            a: "IT, Design, Marketing, Data & Analytics, HR – Freelance oder Festanstellung möglich.",
        },
    ],
    en: [
        {
            q: "How quickly will I see first candidates?",
            a: "It depends on the profile you're looking for. Typically, first matching freelancers get in touch within 24–48 hours of posting.",
        },
        {
            q: "What does it cost as a company?",
            a: "1 project posting per month is free. Additional project postings can be purchased from €49 individually or as a package. We charge no commission on your collaboration with freelancers.",
        },
        {
            q: "How are profiles vetted?",
            a: "Each profile goes through a manual onboarding process: skills, experience, and profile completeness are checked.",
        },
        {
            q: "Is the platform GDPR-compliant?",
            a: "Yes. All data is stored exclusively on EU servers. GDPR compliance is not an add-on – it's standard.",
        },
        {
            q: "For which roles can I post?",
            a: "IT, Design, Marketing, Data & Analytics, HR – freelance or permanent employment.",
        },
    ],
};

export default function CompanyLanding() {
    const router = useRouter();
    const [lang, setLang] = useState("de");

    useEffect(() => {
        const checkUser = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data: profile } = await supabase
                .from("profiles")
                .select("role")
                .eq("id", user.id)
                .single();

            if (profile?.role === "company") {
                router.push("/company-dashboard");
            } else if (profile?.role === "freelancer") {
                router.push("/freelancer-dashboard");
            }
        };

        checkUser();
    }, [router]);

    return (
        <div className="min-h-screen bg-white text-gray-900">

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
                    <Link href="/" className="text-2xl font-bold text-blue-600">
                        FreelanceGen
                    </Link>

                    <nav className="flex items-center gap-4 sm:gap-6">
                        <Link
                            href="#warum"
                            className="text-gray-600 hover:text-blue-600 transition text-sm hidden sm:block"
                        >
                            {lang === "de" ? "Vorteile" : "Benefits"}
                        </Link>

                        <Link
                            href="#so-funktioniert"
                            className="text-gray-600 hover:text-blue-600 transition text-sm hidden sm:block"
                        >
                            {lang === "de" ? "So funktioniert's" : "How it works"}
                        </Link>

                        <Link
                            href="#faq"
                            className="text-gray-600 hover:text-blue-600 transition text-sm hidden sm:block"
                        >
                            FAQ
                        </Link>

                        {/* Language switcher */}
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setLang("de")}
                                className={`px-3 py-1 text-sm rounded-md transition ${
                                    lang === "de"
                                        ? "bg-white shadow text-blue-600"
                                        : "text-gray-700 hover:text-blue-600"
                                }`}
                            >
                                DE
                            </button>

                            <button
                                onClick={() => setLang("en")}
                                className={`px-3 py-1 text-sm rounded-md transition ${
                                    lang === "en"
                                        ? "bg-white shadow text-blue-600"
                                        : "text-gray-700 hover:text-blue-600"
                                }`}
                            >
                                EN
                            </button>
                        </div>

                        <Link
                            href="/login"
                            className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-blue-700 transition text-sm sm:text-base whitespace-nowrap"
                        >
                            Login
                        </Link>
                    </nav>
                </div>
            </header>

            {/* Hero */}
            <section className="relative bg-gradient-to-br from-blue-700 via-blue-600 to-teal-500 text-white pt-28 sm:pt-36 pb-20 sm:pb-28 text-center overflow-hidden">
                <motion.div
                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_60%_40%,rgba(255,255,255,0.07)_0%,transparent_55%)]"
                    animate={{ scale: [1, 1.04, 1] }}
                    transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
                />

                <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
                    <motion.div
                        className="inline-flex items-center gap-2 bg-white/15 border border-white/20 px-4 py-1.5 rounded-full text-sm font-medium mb-8"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        {lang === "de"
                            ? "Für Unternehmen in DE, AT, CH"
                            : "For companies in DE, AT, CH"}
                    </motion.div>

                    <motion.h1
                        className="text-3xl sm:text-5xl md:text-6xl font-bold mb-5 leading-tight"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                    >
                        {lang === "de" ? "Weniger Aufwand." : "Less effort."}
                        <br className="hidden sm:block" />
                        <span className="text-yellow-300">
                            {lang === "de"
                                ? "Schneller zum richtigen Profil."
                                : "Faster to the right profile."}
                        </span>
                    </motion.h1>

                    <motion.p
                        className="text-lg sm:text-xl text-white/85 mb-3 max-w-xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.25 }}
                    >
                        {lang === "de"
                            ? "Manuell geprüfte IT-Spezialisten aus DACH – direkt kontaktierbar, ohne Provision."
                            : "Manually vetted IT specialists from DACH – contactable directly, no commission."}
                    </motion.p>

                    <motion.p
                        className="text-white/75 text-sm mb-6 max-w-xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                    >
                        {lang === "de"
                            ? "Projekt posten → geprüfte Freelancer bewerben sich → Sie wählen direkt"
                            : "Post project → vetted freelancers apply → you choose directly"}
                    </motion.p>

                    <motion.p
                        className="text-white/60 text-sm mb-10"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                    >
                        {lang === "de"
                            ? "Erste passende Profile in der Regel in 24–48h · 1 Projekt pro Monat kostenlos · weitere Veröffentlichungen ab 49 €"
                            : "First matching profiles typically within 24–48h · 1 project per month free · additional postings from €49"}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.45 }}
                        className="flex flex-col sm:flex-row justify-center gap-4"
                    >
                        <div className="flex flex-col items-center w-full sm:w-auto">
                            <Link
                                href="/register/company"
                                className="w-full sm:w-auto"
                            >
                                <Button className="bg-white text-blue-700 hover:bg-gray-50 text-base sm:text-lg px-8 py-5 w-full rounded-xl shadow-lg font-semibold transition-all duration-300">
                                    {lang === "de"
                                        ? "Projekt posten"
                                        : "Post project"}
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </Link>

                            <p className="text-white/60 text-xs mt-2">
                                {lang === "de"
                                    ? "In wenigen Minuten veröffentlicht · 1 Projekt pro Monat kostenlos"
                                    : "Published in minutes · 1 project per month free"}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Trust bar */}
            <section className="bg-gray-50 border-b border-gray-100 py-5">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
                        {[
                            {
                                icon: (
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                ),
                                text:
                                    lang === "de"
                                        ? "Manuell geprüfte DACH-Profile"
                                        : "Manually vetted DACH profiles",
                            },
                            {
                                icon: (
                                    <Euro className="h-4 w-4 text-teal-600" />
                                ),
                                text:
                                    lang === "de"
                                        ? "1 Projekt pro Monat kostenlos · 0 % Provision"
                                        : "1 project per month free · 0% commission",
                            },
                            {
                                icon: (
                                    <Shield className="h-4 w-4 text-blue-600" />
                                ),
                                text:
                                    lang === "de"
                                        ? "DSGVO-konform – EU-Server"
                                        : "GDPR-compliant – EU servers",
                            },
                            {
                                icon: (
                                    <Clock className="h-4 w-4 text-purple-600" />
                                ),
                                text:
                                    lang === "de"
                                        ? "Erste Profile in der Regel in 24–48h"
                                        : "First profiles typically within 24–48h",
                            },
                        ].map((item, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-2 text-sm text-gray-600"
                            >
                                {item.icon}
                                <span>{item.text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY */}
            <section id="warum" className="py-16 sm:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">
                            {lang === "de"
                                ? "Was Sie konkret bekommen"
                                : "What you get"}
                        </h2>

                        <p className="text-gray-500 text-lg">
                            {lang === "de"
                                ? "Kein Recruiting-Overhead. Kein Agenturmodell. Direkter Zugang."
                                : "No recruiting overhead. No agency model. Direct access."}
                        </p>
                    </motion.div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {whyItems[lang as "de" | "en"].map((item, idx) => (
                            <motion.div
                                key={idx}
                                className="bg-gray-50 rounded-2xl p-6 hover:shadow-md transition-all duration-300"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.5,
                                    delay: idx * 0.07,
                                }}
                            >
                                <div className="mb-4">{item.icon}</div>

                                <h3 className="font-bold text-lg mb-2 text-gray-900">
                                    {item.title}
                                </h3>

                                <p className="text-gray-500 text-sm leading-relaxed">
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* VS BLOCK */}
            <section className="py-16 sm:py-20 bg-gray-50 border-t border-gray-100">
                <div className="max-w-5xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">
                            {lang === "de"
                                ? "Wie moderne Freelancer-Suche funktioniert"
                                : "How modern freelancer search works"}
                        </h2>

                        <p className="text-gray-500 text-lg">
                            {lang === "de"
                                ? "Direkter Zugang zu geprüften IT-Profis – ohne unnötige Zwischenschritte."
                                : "Direct access to vetted IT professionals – no unnecessary detours."}
                        </p>

                        <p className="text-gray-500 text-base mb-6">
                            {lang === "de"
                                ? "Der Unterschied liegt nicht im Angebot, sondern in Geschwindigkeit, Qualität und Transparenz."
                                : "The difference isn't in the offering, but in speed, quality, and transparency."}
                        </p>
                    </motion.div>

                    <div className="grid sm:grid-cols-3 gap-6">

                        {/* CHALLENGES */}
                        <motion.div
                            className="rounded-2xl p-6 bg-white border border-gray-200"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="flex items-center gap-2 mb-5">
                                <XCircle className="h-5 w-5 text-gray-400" />

                                <span className="font-bold text-base text-gray-700">
                                    {lang === "de"
                                        ? "Typische Herausforderungen"
                                        : "Typical challenges"}
                                </span>
                            </div>

                            <ul className="space-y-3 text-sm text-gray-500">
                                {(lang === "de"
                                        ? [
                                            "Lange Abstimmungs- und Suchprozesse",
                                            "Viele unpassende oder schwer vergleichbare Profile",
                                            "Zeitaufwendiges Screening durch interne Teams",
                                            "Fehlende Transparenz vor dem ersten Gespräch",
                                        ]
                                        : [
                                            "Long coordination and search processes",
                                            "Many mismatched or hard-to-compare profiles",
                                            "Time-consuming screening by internal teams",
                                            "Lack of transparency before the first call",
                                        ]
                                ).map((point, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-2"
                                    >
                                        <XCircle className="h-4 w-4 text-gray-300 mt-0.5 flex-shrink-0" />
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* FREELANCEGEN */}
                        <motion.div
                            className="rounded-2xl p-6 bg-blue-600 border border-blue-600 text-white shadow-xl"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            <div className="flex items-center gap-2 mb-5">
                                <CheckCircle className="h-5 w-5 text-white" />

                                <span className="font-bold text-base text-white">
                                    FreelanceGen
                                </span>
                            </div>

                            <ul className="space-y-3 text-sm text-white/90">
                                {(lang === "de"
                                        ? [
                                            "Geprüfte IT-Freelancer aus DACH",
                                            "Passende Profile in der Regel in 24–48h",
                                            "Direkter Zugang ohne Umwege",
                                            "Klare Profile vor dem ersten Kontakt",
                                        ]
                                        : [
                                            "Vetted IT freelancers from DACH",
                                            "Matching profiles typically in 24–48h",
                                            "Direct access without detours",
                                            "Clear profiles before first contact",
                                        ]
                                ).map((point, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-2"
                                    >
                                        <CheckCircle className="h-4 w-4 text-green-300 mt-0.5 flex-shrink-0" />
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* RESULT */}
                        <motion.div
                            className="rounded-2xl p-6 bg-white border border-gray-200"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <div className="flex items-center gap-2 mb-5">
                                <CheckCircle className="h-5 w-5 text-green-500" />

                                <span className="font-bold text-base text-gray-700">
                                    {lang === "de" ? "Ergebnis" : "Result"}
                                </span>
                            </div>

                            <ul className="space-y-3 text-sm text-gray-500">
                                {(lang === "de"
                                        ? [
                                            "Kürzere Time-to-Hire",
                                            "Weniger Screening-Aufwand im Team",
                                            "Höhere Passgenauigkeit der Kandidaten",
                                            "Mehr Kontrolle im Auswahlprozess",
                                        ]
                                        : [
                                            "Shorter time-to-hire",
                                            "Less screening effort for your team",
                                            "Higher candidate fit accuracy",
                                            "More control in the selection process",
                                        ]
                                ).map((point, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-2"
                                    >
                                        <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section
                id="so-funktioniert"
                className="py-16 sm:py-20 bg-white border-t border-gray-100"
            >
                <div className="max-w-4xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">
                            {lang === "de"
                                ? "Von Bedarf zu Kandidat – in 3 Schritten"
                                : "From need to candidate – in 3 steps"}
                        </h2>

                        <p className="text-gray-500 text-lg">
                            {lang === "de"
                                ? "Kein komplizierter Prozess. Kein Sales-Call vorab."
                                : "No complicated process. No sales call required."}
                        </p>
                    </motion.div>

                    <div className="grid sm:grid-cols-3 gap-8">
                        {steps[lang as "de" | "en"].map((s, idx) => (
                            <motion.div
                                key={idx}
                                className="text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.5,
                                    delay: idx * 0.1,
                                }}
                            >
                                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4 shadow-md">
                                    {s.step}
                                </div>

                                <h3 className="font-bold text-lg mb-2 text-gray-900">
                                    {s.title}
                                </h3>

                                <p className="text-gray-500 text-sm leading-relaxed">
                                    {s.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <Link href="/register/company">
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-xl text-base font-semibold shadow-md">
                                {lang === "de"
                                    ? "Projekt posten"
                                    : "Post project"}
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* USE CASES */}
            <section className="py-16 sm:py-20 bg-gray-50 border-t border-gray-100">
                <div className="max-w-5xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">
                            {lang === "de"
                                ? "Für welche Rollen?"
                                : "For which roles?"}
                        </h2>

                        <p className="text-gray-500 text-lg">
                            {lang === "de"
                                ? "Projektbasiert oder laufend – Sie definieren den Bedarf."
                                : "Project-based or ongoing – you define the need."}
                        </p>
                    </motion.div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {useCases[lang as "de" | "en"].map((uc, idx) => (
                            <motion.div
                                key={idx}
                                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-all duration-300"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.5,
                                    delay: idx * 0.07,
                                }}
                            >
                                <div className="mb-3">{uc.icon}</div>

                                <h3 className="font-bold text-base mb-1 text-gray-900">
                                    {uc.label}
                                </h3>

                                <p className="text-gray-500 text-sm leading-relaxed">
                                    {uc.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="text-center mt-10">
                        <Link href="/register/company">
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-xl text-base font-semibold shadow-md">
                                {lang === "de"
                                    ? "Projekt posten"
                                    : "Post project"}
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* WORK MODELS */}
            <section className="py-14 bg-white border-t border-gray-100">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                            {lang === "de"
                                ? "Remote, Hybrid oder Vor Ort"
                                : "Remote, Hybrid or On-site"}
                        </h2>

                        <p className="text-gray-500 mb-8">
                            {lang === "de"
                                ? "Sie definieren das Modell – Profile sind entsprechend gefiltert."
                                : "You define the model – profiles are filtered accordingly."}
                        </p>
                    </motion.div>

                    <div className="grid sm:grid-cols-3 gap-4">
                        {workModels[lang as "de" | "en"].map((wm, idx) => (
                            <motion.div
                                key={idx}
                                className="bg-gray-50 border border-gray-100 rounded-xl p-5 text-left"
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.4,
                                    delay: idx * 0.08,
                                }}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <MapPin className="h-4 w-4 text-blue-500" />
                                    <span className="font-semibold text-gray-900">
                                        {wm.label}
                                    </span>
                                </div>

                                <p className="text-gray-500 text-sm">
                                    {wm.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TYPISCHE PROJEKTE */}
            <section
                id="talente"
                className="py-16 sm:py-20 bg-gray-50 border-t border-gray-100"
            >
                <div className="max-w-6xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-10"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold mb-3">
                            {lang === "de"
                                ? "Typische Projekte"
                                : "Typical projects"}
                        </h2>

                        <p className="text-gray-500 text-lg">
                            {lang === "de"
                                ? "Häufig gesuchte Rollen – für diese Anfragen sind passende Profile verfügbar."
                                : "Frequently sought roles – matching profiles are available for these requests."}
                        </p>
                    </motion.div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {typicalProjects[lang as "de" | "en"].map((p, i) => (
                            <motion.div
                                key={i}
                                className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition"
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.4,
                                    delay: i * 0.06,
                                }}
                            >
                                <h3 className="font-semibold text-base mb-1 text-gray-900">
                                    {p.title}
                                </h3>

                                <p className="text-xs text-gray-400 mb-4 flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {p.location}
                                </p>

                                <div className="flex justify-between items-center text-sm">
                                    <span className="font-medium text-green-600">
                                        {p.rate}
                                    </span>

                                    <span className="text-gray-400 flex items-center gap-1 text-xs">
                                        <Clock className="h-3 w-3" />
                                        {p.label}
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="text-center mt-10">
                        <Link href="/register/company">
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-xl text-base font-semibold shadow-md">
                                {lang === "de"
                                    ? "Projekt posten"
                                    : "Post project"}
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                id="faq"
                className="py-16 sm:py-20 bg-white border-t border-gray-100"
            >
                <div className="max-w-3xl mx-auto px-4">
                    <motion.h2
                        className="text-3xl sm:text-4xl font-bold text-center mb-10"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        {lang === "de"
                            ? "Häufige Fragen"
                            : "Frequently asked questions"}
                    </motion.h2>

                    <div className="space-y-6">
                        {faqs[lang as "de" | "en"].map((faq, idx) => (
                            <motion.div
                                key={idx}
                                className="border-b border-gray-200 pb-6"
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.4,
                                    delay: idx * 0.07,
                                }}
                            >
                                <h3 className="font-semibold text-gray-900 flex items-start gap-3 mb-2">
                                    <HelpCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                    {faq.q}
                                </h3>

                                <p className="text-gray-500 text-sm leading-relaxed pl-8">
                                    {faq.a}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-20 sm:py-24 bg-gradient-to-br from-blue-700 via-blue-600 to-teal-500 text-white text-center">
                <div className="max-w-3xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                            {lang === "de"
                                ? "Weniger Aufwand."
                                : "Less effort."}
                            <br className="hidden sm:block" />
                            {lang === "de"
                                ? "Schneller zum richtigen Kandidaten."
                                : "Faster to the right candidate."}
                        </h2>

                        <p className="text-lg text-white/85 mb-2">
                            {lang === "de"
                                ? "Manuell geprüfte IT-Profile aus DACH. Direkt kontaktierbar."
                                : "Manually vetted IT profiles from DACH. Directly contactable."}
                        </p>

                        <p className="text-white/60 text-sm mb-10">
                            {lang === "de"
                                ? "1 Projekt pro Monat kostenlos · weitere Veröffentlichungen ab 49 € · 0 % Provision"
                                : "1 project per month free · additional postings from €49 · 0% commission"}
                        </p>

                        <Link
                            href="/register/company"
                            className="inline-block w-full sm:w-auto"
                        >
                            <Button className="bg-white text-blue-700 hover:bg-gray-50 text-lg px-12 py-6 rounded-xl shadow-2xl font-semibold transition-all duration-300 w-full sm:w-auto">
                                {lang === "de"
                                    ? "Projekt posten"
                                    : "Post project"}
                                <ArrowRight className="h-5 w-5 ml-2" />
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            <CookieBanner />
        </div>
    );
}